import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { listJoinableSchools, createLearnerProfile } from "@/lib/learner-onboarding.functions";
import { Loader2, GraduationCap, ArrowRight, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/learner/onboarding")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Set up your learner profile — MAKAI" }] }),
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const listSchools = useServerFn(listJoinableSchools);
  const createProfile = useServerFn(createLearnerProfile);
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [schoolId, setSchoolId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [admissionNumber, setAdmissionNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listSchools()
      .then((r) => setSchools(r.schools))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [listSchools]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createProfile({ data: { schoolId, firstName, lastName, admissionNumber: admissionNumber || undefined } });
      toast.success("Profile created!", { description: "Taking you to your dashboard…" });
      setSubmitting(false);
      setSuccess(true);
      setTimeout(() => navigate({ to: "/learner" }), 1600);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <AppShell role="learner" title="Set up your learner profile" subtitle="Tell us where you study so we can personalise everything.">
      <div className="mx-auto max-w-xl">
        {loading ? (
          <div className="grid place-items-center py-20 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : success ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center shadow-card">
            <span className="mx-auto inline-grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h3 className="mt-4 font-display text-xl font-bold">You're all set, {firstName}!</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Your learner profile has been created. Redirecting you to your dashboard…
            </p>
            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading dashboard
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="mb-5 flex items-center gap-3">
              <span className="inline-grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
                <GraduationCap className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-lg font-bold">Create your learner profile</h2>
                <p className="text-xs text-muted-foreground">Linking a school is optional — you can revise on your own and join a school later.</p>
              </div>
            </div>

            <div className="space-y-4">
              <Field label="School (optional)">
                <select
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                >
                  <option value="">I'm just revising on my own</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {schools.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    No schools registered yet — that's fine, keep going to set up a personal revision account.
                  </p>
                )}
              </Field>


              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name">
                  <input
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                  />
                </Field>
              </div>

              <Field label="Admission number (optional)">
                <input
                  value={admissionNumber}
                  onChange={(e) => setAdmissionNumber(e.target.value)}
                  placeholder="e.g. 2026/0457"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                />
              </Field>
            </div>

            {error && (
              <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {submitting ? "Creating profile…" : "Create my profile"}
            </button>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Your school admin can later assign you to a class and stream.
            </p>
          </form>
        )}
      </div>
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
